import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Globe, Phone, Mail, MapPin, Users, Tag,
  ExternalLink, Pencil, Link2, Unlink, Briefcase,
  Linkedin, Hash, StickyNote, ChevronDown, ChevronUp,
  AlertCircle, RefreshCw, Plus,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useCompanyDetail, useCompanyContacts, useLinkAccount, useUnlinkAccount, companyDetailKeys } from '../hooks/useCrmCompanyDetail';
import { useUpdateCompany } from '../hooks/useCrmCompanies';
import { useCreateContact, useUpdateContact } from '../hooks/useCrmContacts';
import { useCompanySites, useAddCompanySite } from '../hooks/useCompanySites';
import type { Company, Contact, CompanyType, CompanyStatus, CreateCompanyInput, CreateContactInput, CompanySite } from '../types';
import { CrmDetailLayout } from '../components/CrmDetailLayout';
import { CrmNoteFeed } from '../components/notes/CrmNoteFeed';
import { ContactModal } from '../components/ContactModal';
import { CompanyLogoField } from '../components/CompanyLogoField';

// =============================================================================
// Constants
// =============================================================================

const TYPE_COLORS: Record<CompanyType, string> = {
  customer: 'bg-green-100 text-green-800',
  prospect: 'bg-blue-100 text-blue-800',
  supplier: 'bg-amber-100 text-amber-800',
  partner: 'bg-purple-100 text-purple-800',
  competitor: 'bg-red-100 text-red-800',
  irrelevant: 'bg-slate-100 text-slate-500',
  other: 'bg-gray-100 text-gray-800',
};

const STATUS_COLORS: Record<CompanyStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  archived: 'bg-red-100 text-red-800',
};

// =============================================================================
// InfoRow helper
// =============================================================================

function InfoRow({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border/50 last:border-0">
      {icon && <span className="mt-0.5 text-muted-foreground">{icon}</span>}
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-sm break-words">{value}</div>
      </div>
    </div>
  );
}

// =============================================================================
// CompanyInfoSidebar
// =============================================================================

function CompanyInfoSidebar({ company, onEdit }: { company: Company; onEdit: () => void }) {
  const { t } = useTranslation('crm');
  const [notesExpanded, setNotesExpanded] = useState(false);
  const NOTES_LIMIT = 150;
  const hasLongNotes = (company.notes?.length ?? 0) > NOTES_LIMIT;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('companies.info.cardTitle')}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5 mr-1" /> {t('common.edit')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-0">
        <InfoRow
          label={t('companies.info.type')}
          icon={<Tag className="h-3.5 w-3.5" />}
          value={<Badge variant="outline" className={TYPE_COLORS[company.type]}>{t(`companies.type.${company.type}`)}</Badge>}
        />
        <InfoRow
          label={t('companies.info.status')}
          icon={<Hash className="h-3.5 w-3.5" />}
          value={<Badge variant="outline" className={STATUS_COLORS[company.status]}>{t(`companies.status.${company.status}`)}</Badge>}
        />
        <InfoRow label={t('companies.info.industry')} icon={<Briefcase className="h-3.5 w-3.5" />} value={company.industry} />
        <InfoRow label={t('companies.info.domain')} icon={<Globe className="h-3.5 w-3.5" />} value={company.domain} />
        <InfoRow
          label={t('companies.info.website')}
          icon={<Globe className="h-3.5 w-3.5" />}
          value={
            company.website ? (
              <a
                href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                {company.website} <ExternalLink className="h-3 w-3" />
              </a>
            ) : null
          }
        />
        <InfoRow
          label={t('companies.info.linkedin')}
          icon={<Linkedin className="h-3.5 w-3.5" />}
          value={
            company.linkedin_url ? (
              <a
                href={company.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                {company.linkedin_url.replace(/^https?:\/\/(www\.)?linkedin\.com\//, '')}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : null
          }
        />
        <InfoRow
          label={t('companies.info.phone')}
          icon={<Phone className="h-3.5 w-3.5" />}
          value={company.phone}
        />
        <InfoRow
          label={t('companies.info.email')}
          icon={<Mail className="h-3.5 w-3.5" />}
          value={
            company.email ? (
              <a href={`mailto:${company.email}`} className="text-primary hover:underline">
                {company.email}
              </a>
            ) : null
          }
        />
        <InfoRow
          label={t('companies.info.address')}
          icon={<MapPin className="h-3.5 w-3.5" />}
          value={
            [company.city, company.province, company.country].filter(Boolean).join(', ') || null
          }
        />
        <InfoRow
          label={t('companies.info.employees')}
          icon={<Users className="h-3.5 w-3.5" />}
          value={company.employee_count != null ? company.employee_count.toLocaleString() : null}
        />
        <InfoRow
          label={t('companies.info.annualRevenue')}
          value={company.annual_revenue != null ? formatCurrency(company.annual_revenue) : null}
        />
        {company.hubspot_company_id && (
          <InfoRow label={t('companies.info.hubspotId')} value={<span className="text-xs font-mono">{company.hubspot_company_id}</span>} />
        )}
        {company.apollo_company_id && (
          <InfoRow label={t('companies.info.apolloId')} value={<span className="text-xs font-mono">{company.apollo_company_id}</span>} />
        )}
        {company.tags && company.tags.length > 0 && (
          <InfoRow
            label={t('companies.info.tags')}
            icon={<Tag className="h-3.5 w-3.5" />}
            value={
              <div className="flex flex-wrap gap-1 mt-0.5">
                {company.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                ))}
              </div>
            }
          />
        )}
        {company.notes && (
          <InfoRow
            label={t('companies.info.notes')}
            icon={<StickyNote className="h-3.5 w-3.5" />}
            value={
              <div>
                <p className="whitespace-pre-wrap text-sm">
                  {notesExpanded || !hasLongNotes
                    ? company.notes
                    : `${company.notes.slice(0, NOTES_LIMIT)}...`}
                </p>
                {hasLongNotes && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-xs"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                  >
                    {notesExpanded ? (
                      <>{t('common.showLess')} <ChevronUp className="h-3 w-3 ml-0.5" /></>
                    ) : (
                      <>{t('common.showMore')} <ChevronDown className="h-3 w-3 ml-0.5" /></>
                    )}
                  </Button>
                )}
              </div>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// CompanyContactsPanel
// =============================================================================

function CompanyContactsPanel({
  contacts,
  total,
  onAdd,
  onEdit,
}: {
  contacts: Contact[];
  total: number;
  onAdd: () => void;
  onEdit: (contact: Contact) => void;
}) {
  const { t } = useTranslation('crm');
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('companies.contacts.cardTitle', { total })}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onAdd}>
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('companies.contacts.add')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Users className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
            <p className="text-sm">{t('companies.contacts.noContacts')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact) => (
              <div key={contact.id} className="group flex items-start justify-between gap-2 border-b border-border/50 last:border-0 pb-2 last:pb-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {contact.first_name} {contact.last_name}
                    </span>
                    {contact.is_primary && (
                      <Badge variant="outline" className="text-[10px] py-0 bg-blue-50 text-blue-700">{t('common.primaryBadge')}</Badge>
                    )}
                  </div>
                  {contact.title && (
                    <p className="text-xs text-muted-foreground">{contact.title}</p>
                  )}
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-0.5">
                      <Mail className="h-3 w-3" /> {contact.email}
                    </a>
                  )}
                  {contact.phone && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Phone className="h-3 w-3" /> {contact.phone}
                    </span>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                  aria-label={t('companies.contacts.editAria', { name: `${contact.first_name} ${contact.last_name}` })}
                  onClick={() => onEdit(contact)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// CompanySitesPanel — list + add sites (public.company_sites, migration 0273)
// =============================================================================

function CompanySitesPanel({ company }: { company: Company }) {
  const { t } = useTranslation('crm');
  const { data: sites, isLoading } = useCompanySites(company.id);
  const addMutation = useAddCompanySite(company.id);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [province, setProvince] = useState('');

  const reset = () => { setName(''); setCity(''); setAddress(''); setProvince(''); setAdding(false); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await addMutation.mutateAsync({
      name: name.trim(),
      address: address || undefined,
      city: city || undefined,
      province: province || undefined,
    });
    reset();
  };

  const list: CompanySite[] = sites ?? [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('companies.sites.cardTitle', { total: list.length })}</CardTitle>
          {!adding && (
            <Button variant="ghost" size="sm" onClick={() => setAdding(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" /> {t('companies.sites.add')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {adding && (
          <form onSubmit={handleSubmit} className="space-y-2 rounded border p-2.5 bg-muted/20">
            <div>
              <Label htmlFor="site-name" className="text-xs">{t('companies.sites.name')}</Label>
              <Input id="site-name" className="h-8" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="site-city" className="text-xs">{t('companies.sites.city')}</Label>
                <Input id="site-city" className="h-8" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="site-province" className="text-xs">{t('companies.sites.province')}</Label>
                <Input id="site-province" className="h-8" value={province} onChange={(e) => setProvince(e.target.value)} />
              </div>
            </div>
            <div>
              <Label htmlFor="site-address" className="text-xs">{t('companies.sites.address')}</Label>
              <Input id="site-address" className="h-8" value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-0.5">
              <Button type="button" variant="ghost" size="sm" onClick={reset}>{t('common.cancel')}</Button>
              <Button type="submit" size="sm" disabled={!name.trim() || addMutation.isPending}>
                {addMutation.isPending ? t('common.saving') : t('common.add')}
              </Button>
            </div>
          </form>
        )}

        {isLoading ? (
          <Skeleton className="h-12" />
        ) : list.length === 0 ? (
          !adding && (
            <div className="text-center py-6 text-muted-foreground">
              <MapPin className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
              <p className="text-sm">{t('companies.sites.noSites')}</p>
            </div>
          )
        ) : (
          <div className="space-y-2">
            {list.map((site) => (
              <div key={site.id} className="flex items-start gap-2 border-b border-border/50 last:border-0 pb-2 last:pb-0">
                <MapPin className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="font-medium text-sm">{site.name}</div>
                  {(site.address || site.city || site.province) && (
                    <p className="text-xs text-muted-foreground">
                      {[site.address, site.city, site.province].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// AccountSearchCombobox
// =============================================================================

interface AccountOption {
  id: string;
  name: string;
  accountNumber?: string;
}

function AccountSearchCombobox({ onSelect }: { onSelect: (accountId: string) => void }) {
  const { t } = useTranslation('crm');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.get<{ data: AccountOption[]; total: number }>('/api/v4/accounts', {
          search: search || undefined,
          pageLength: 10,
        });
        setAccounts(data.data);
      } catch {
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start">
          <Link2 className="h-3.5 w-3.5 mr-1.5" /> {t('companies.links.linkAccount')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={t('companies.links.searchAccounts')} value={search} onValueChange={setSearch} />
          <CommandList>
            <CommandEmpty>{loading ? t('companies.links.searching') : t('companies.links.noAccountsFound')}</CommandEmpty>
            <CommandGroup>
              {accounts.map((acc) => (
                <CommandItem
                  key={acc.id}
                  value={acc.id}
                  onSelect={() => {
                    onSelect(acc.id);
                    setOpen(false);
                  }}
                >
                  <div>
                    <div className="text-sm font-medium">{acc.name}</div>
                    {acc.accountNumber && (
                      <div className="text-xs text-muted-foreground">{acc.accountNumber}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// CompanyLinksPanel
// =============================================================================

function CompanyLinksPanel({
  company,
  onLink,
  onUnlink,
  isLinking,
  isUnlinking,
}: {
  company: Company;
  onLink: (accountId: string) => void;
  onUnlink: () => void;
  isLinking: boolean;
  isUnlinking: boolean;
}) {
  const { t } = useTranslation('crm');
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('companies.links.cardTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Linked Account */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t('companies.links.linkedAccount')}</div>
          {company.account_id ? (
            <div className="flex items-center justify-between gap-2 p-2 rounded border bg-muted/30">
              <a
                href={`/accounts/${company.account_id}`}
                className="text-sm text-primary hover:underline font-medium truncate"
              >
                {t('companies.links.viewAccount')}
              </a>
              <AlertDialog open={showUnlinkConfirm} onOpenChange={setShowUnlinkConfirm}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('companies.links.unlinkTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('companies.links.unlinkDescription')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        onUnlink();
                        setShowUnlinkConfirm(false);
                      }}
                    >
                      {t('companies.links.unlink')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setShowUnlinkConfirm(true)}
                disabled={isUnlinking}
              >
                <Unlink className="h-3.5 w-3.5 mr-1" />
                {isUnlinking ? t('companies.links.unlinking') : t('companies.links.unlink')}
              </Button>
            </div>
          ) : (
            <AccountSearchCombobox onSelect={onLink} />
          )}
          {isLinking && <p className="text-xs text-muted-foreground mt-1">{t('companies.links.linking')}</p>}
        </div>

        {/* External Links */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t('companies.links.externalLinks')}</div>
          <div className="space-y-1.5">
            {company.hubspot_company_id && (
              <a
                href={`https://app.hubspot.com/contacts/22484617/record/0-2/${company.hubspot_company_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> HubSpot
              </a>
            )}
            {company.linkedin_url && (
              <a
                href={company.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Linkedin className="h-3.5 w-3.5" /> LinkedIn
              </a>
            )}
            {company.website && (
              <a
                href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Globe className="h-3.5 w-3.5" /> Website
              </a>
            )}
            {!company.hubspot_company_id && !company.linkedin_url && !company.website && (
              <p className="text-sm text-muted-foreground">{t('companies.links.noExternalLinks')}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// CompanyModal (edit)
// =============================================================================

function CompanyModal({
  open,
  onOpenChange,
  company,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: Company | null;
  onSave: (input: CreateCompanyInput) => Promise<void>;
  isSaving: boolean;
}) {
  const { t } = useTranslation('crm');
  const [name, setName] = useState('');
  const [type, setType] = useState<CompanyType>('prospect');
  const [status, setStatus] = useState<CompanyStatus>('active');
  const [industry, setIndustry] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [domain, setDomain] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (open && company) {
      setName(company.name);
      setType(company.type);
      setStatus(company.status);
      setIndustry(company.industry || '');
      setWebsite(company.website || '');
      setPhone(company.phone || '');
      setEmail(company.email || '');
      setCity(company.city || '');
      setProvince(company.province || '');
      setDomain(company.domain || '');
      setLinkedinUrl(company.linkedin_url || '');
      setNotes(company.notes || '');
    } else if (open && !company) {
      setName(''); setType('prospect'); setStatus('active'); setIndustry('');
      setWebsite(''); setPhone(''); setEmail(''); setCity(''); setProvince('');
      setDomain(''); setLinkedinUrl(''); setNotes('');
    }
  }, [open, company]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name,
      type,
      status,
      industry: industry || undefined,
      website: website || undefined,
      phone: phone || undefined,
      email: email || undefined,
      city: city || undefined,
      province: province || undefined,
      domain: domain || undefined,
      linkedinUrl: linkedinUrl || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('companies.modal.titleEdit')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>{t('companies.modal.name')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label>{t('companies.modal.type')}</Label>
              <Select value={type} onValueChange={(v) => setType(v as CompanyType)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">{t('companies.type.prospect')}</SelectItem>
                  <SelectItem value="customer">{t('companies.type.customer')}</SelectItem>
                  <SelectItem value="supplier">{t('companies.type.supplier')}</SelectItem>
                  <SelectItem value="partner">{t('companies.type.partner')}</SelectItem>
                  <SelectItem value="competitor">{t('companies.type.competitor')}</SelectItem>
                  <SelectItem value="irrelevant">{t('companies.type.irrelevant')}</SelectItem>
                  <SelectItem value="other">{t('companies.type.other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('companies.modal.status')}</Label>
              <Select value={status} onValueChange={(v) => setStatus(v as CompanyStatus)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('companies.status.active')}</SelectItem>
                  <SelectItem value="inactive">{t('companies.status.inactive')}</SelectItem>
                  <SelectItem value="archived">{t('companies.status.archived')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('companies.modal.industry')}</Label>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.domain')}</Label>
              <Input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder={t('companies.modal.domainPlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.website')}</Label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder={t('companies.modal.websitePlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.linkedin')}</Label>
              <Input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder={t('companies.modal.linkedinPlaceholder')} />
            </div>
            <div>
              <Label>{t('companies.modal.phone')}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.email')}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.city')}</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div>
              <Label>{t('companies.modal.province')}</Label>
              <Input value={province} onChange={(e) => setProvince(e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>{t('companies.modal.notes')}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!name.trim() || isSaving}>
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Loading Skeleton
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-5 w-16" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className="lg:col-span-1"><Skeleton className="h-[400px]" /></div>
        <div className="lg:col-span-2"><Skeleton className="h-[400px]" /></div>
        <div className="lg:col-span-1 space-y-4">
          <Skeleton className="h-[200px]" />
          <Skeleton className="h-[200px]" />
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Not Found
// =============================================================================

function NotFound() {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
      <Building2 className="h-12 w-12 text-muted-foreground opacity-40" />
      <h2 className="text-lg font-semibold">{t('companies.notFound')}</h2>
      <Button variant="outline" onClick={() => navigate('/crm/companies')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t('companies.notFoundBack')}
      </Button>
    </div>
  );
}

// =============================================================================
// Fetch Error (non-404)
// =============================================================================

function FetchError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('crm');
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-4">
      <Alert variant="destructive" className="max-w-md">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t('companies.failedToLoadDetail')}</AlertTitle>
        <AlertDescription className="mt-1">
          {t('common.somethingWentWrongRetry')}
        </AlertDescription>
      </Alert>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-1" /> {t('common.retry')}
        </Button>
        <Button variant="ghost" onClick={() => navigate('/crm/companies')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t('companies.notFoundBack')}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// CrmCompanyDetailPage
// =============================================================================

export function CrmCompanyDetailPage() {
  const { t } = useTranslation('crm');
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: company, isLoading, isError, error, refetch } = useCompanyDetail(companyId!);
  const { data: contactsData } = useCompanyContacts(companyId!);
  const updateMutation = useUpdateCompany();
  const linkMutation = useLinkAccount();
  const unlinkMutation = useUnlinkAccount();
  const createContactMutation = useCreateContact();
  const updateContactMutation = useUpdateContact();

  const [showEditModal, setShowEditModal] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);

  const handleSave = useCallback(async (input: CreateCompanyInput) => {
    if (!companyId) return;
    await updateMutation.mutateAsync({ id: companyId, ...input });
    setShowEditModal(false);
  }, [companyId, updateMutation]);

  const handleAddContact = useCallback(() => {
    setEditingContact(null);
    setContactModalOpen(true);
  }, []);

  const handleEditContact = useCallback((contact: Contact) => {
    setEditingContact(contact);
    setContactModalOpen(true);
  }, []);

  const handleSaveContact = useCallback(async (input: CreateContactInput) => {
    if (!companyId) return;
    if (editingContact) {
      await updateContactMutation.mutateAsync({ id: editingContact.id, ...input });
    } else {
      await createContactMutation.mutateAsync(input);
    }
    // The contact mutations invalidate the contacts-list keys; the company-detail
    // contacts panel reads a different key, so refresh it explicitly.
    queryClient.invalidateQueries({ queryKey: companyDetailKeys.contacts(companyId) });
    setContactModalOpen(false);
    setEditingContact(null);
  }, [companyId, editingContact, createContactMutation, updateContactMutation, queryClient]);

  const handleLink = useCallback((accountId: string) => {
    if (!companyId) return;
    linkMutation.mutate({ companyId, accountId });
  }, [companyId, linkMutation]);

  const handleUnlink = useCallback(() => {
    if (!companyId) return;
    unlinkMutation.mutate(companyId);
  }, [companyId, unlinkMutation]);

  if (isLoading) return <LoadingSkeleton />;
  if (isError) {
    const status = (error as { status?: number } | null)?.status;
    if (status === 404) return <NotFound />;
    return <FetchError onRetry={() => refetch()} />;
  }
  if (!company) return <NotFound />;

  return (
    <>
      <CrmDetailLayout
        header={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/crm/companies')}>
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('companies.pageTitle')}
            </Button>
            <CompanyLogoField companyId={company.id} logoUrl={company.logo_url} domain={company.domain} />
            <h1 className="text-xl font-semibold">{company.name}</h1>
            <Badge variant="outline" className={TYPE_COLORS[company.type]}>{company.type}</Badge>
            <Badge variant="outline" className={STATUS_COLORS[company.status]}>{company.status}</Badge>
          </div>
        }
        left={<CompanyInfoSidebar company={company} onEdit={() => setShowEditModal(true)} />}
        center={
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('companies.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CrmNoteFeed entityType="company" entityId={companyId!} />
            </CardContent>
          </Card>
        }
        right={
          <>
            <CompanyContactsPanel
              contacts={contactsData?.items ?? []}
              total={contactsData?.total ?? 0}
              onAdd={handleAddContact}
              onEdit={handleEditContact}
            />
            <CompanySitesPanel company={company} />
            <CompanyLinksPanel
              company={company}
              onLink={handleLink}
              onUnlink={handleUnlink}
              isLinking={linkMutation.isPending}
              isUnlinking={unlinkMutation.isPending}
            />
          </>
        }
      />

      <CompanyModal
        open={showEditModal}
        onOpenChange={(v) => setShowEditModal(v)}
        company={company}
        onSave={handleSave}
        isSaving={updateMutation.isPending}
      />

      <ContactModal
        open={contactModalOpen}
        onOpenChange={(v) => { setContactModalOpen(v); if (!v) setEditingContact(null); }}
        contact={editingContact}
        onSave={handleSaveContact}
        isSaving={createContactMutation.isPending || updateContactMutation.isPending}
        lockedCompany={{ id: company.id, name: company.name }}
      />
    </>
  );
}
